import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpUtils } from '../shared/http.utils';
import { AssetFilters } from '../model/filters/asset-filters';
import { AssetsResponse } from '../model/responses/assets-response';
import { AssetPersistRequest } from '../model/requests/asset-persist-request';
import {AssetMeasurementFilters} from '../model/filters/asset-measurement-filters';

@Injectable({
  providedIn: 'root'
})
export class AssetsService {

  private readonly assetsURL: string;

  constructor(private http: HttpClient) {
    this.assetsURL = '/catalog/assets';
  }

  public getAsset(assetUuid: string): Observable<any> {
    const url = `${this.assetsURL}/${assetUuid}`;
    return this.http.get<any>(url);
  }

  public createAsset(creationData: AssetPersistRequest): Observable<any> {
    const url = `${this.assetsURL}/create`;
    return this.http.post<any>(url, creationData);
  }

  public editAsset(editionData: AssetPersistRequest): Observable<any> {
    const url = `${this.assetsURL}/edit`;
    return this.http.post<any>(url, editionData);
  }

  public deleteAsset(assetUuid: string): Observable<HttpResponse<any>> {
    const url = `${this.assetsURL}/${assetUuid}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public deleteAssets(uuids: string[]): Observable<HttpResponse<any>> {
    const url = `${this.assetsURL}/delete`;
    return this.http.post<any>(url, uuids, {observe: 'response'});
  }

  public getAssets(offset?: number, limit?: number, filters?: AssetFilters): Observable<AssetsResponse>{
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const body = filters ?? new AssetFilters();
    return this.http.post<AssetsResponse>(this.assetsURL, body, options);
  }

  public getAssetsFromUuid(assetsUuid: string[], offset?: number, limit?: number): Observable<AssetsResponse>{
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.assetsURL}/getFromUuid`;
    return this.http.post<AssetsResponse>(url, assetsUuid, options);
  }

  public getMapAssets(): Observable<any> {
    const url = `${this.assetsURL}/map`;
    return this.http.get<any>(url);
  }

  public getAssetProperties(assetUuid: string): Observable<any> {
    const url = `${this.assetsURL}/${assetUuid}/properties`;
    return this.http.get<any>(url);
  }

  public getAssetMeasurement(assetMeasurementUuid: string): Observable<any> {
    const url = `${this.assetsURL}/measurements/${assetMeasurementUuid}`;
    return this.http.get<any>(url);
  }

  public getAssetMeasurements(assetMeasurementUuids: string[]): Observable<any> {
    const url = `${this.assetsURL}/measurements`;
    return this.http.post<any>(url, assetMeasurementUuids);
  }

  public getAssetMeasurementsOfAsset(assetUuid: string): Observable<any> {
    const url = `${this.assetsURL}/${assetUuid}/measurements`;
    return this.http.get<any>(url);
  }

  public getActivatedAssetAndDeviceTriggers(assetUuid: string): Observable<any> {
    const url = `${this.assetsURL}/${assetUuid}/active-asset-triggers`;
    return this.http.get<any>(url);
  }

  public getAssetMeasurementsOfAssets(assetUuids: string[]): Observable<any> {
    const url = `${this.assetsURL}/measurements-of-assets`;
    return this.http.post<any>(url, assetUuids);
  }

  public getCommonAssetMeasurements(filters: AssetMeasurementFilters, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.assetsURL}/commonMeasurements`;
    return this.http.post<any>(url, filters, options);
  }

  public getAssetDevices(assetUuid: string): Observable<any> {
    const url = `${this.assetsURL}/${assetUuid}/devices`;
    return this.http.get<any>(url);
  }

  public getAssetTriggers(assetUuid: string, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.assetsURL}/${assetUuid}/triggers`;
    return this.http.get<any>(url, options);
  }

  public getAssetSummaries(): Observable<any> {
    const url = `${this.assetsURL}/summary`;
    return this.http.get<any>(url);
  }

  public getAssetSummariesWithIconByUuids(uuids: string[]): Observable<any> {
    const url = `${this.assetsURL}/summary-with-icon`;
    return this.http.post<any>(url, uuids);
  }

  public getAssetDeviceMeasurements(assetUuid: string): Observable<any> {
    const url = `${this.assetsURL}/${assetUuid}/device-measurements`;
    return this.http.get<any>(url);
  }

  public getAssetActiveDeviceMeasurements(assetUuid: string, from: number): Observable<any> {
    const url = `${this.assetsURL}/${assetUuid}/active-device-measurements`;
    const params = new HttpParams().append('from', from.toString());
    return this.http.get<any>(url, {params});
  }

  public getAssetActiveAssetMeasurements(assetUuid: string, from: number): Observable<any> {
    const url = `${this.assetsURL}/${assetUuid}/active-asset-measurements`;
    const params = new HttpParams().append('from', from.toString());
    return this.http.get<any>(url, {params});
  }

}
